import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiService from '../../services/api';
import dateUtils from '../../utils/dateUtils';
import AddSupplier from './AddSupplier';
import AddProduct from '../../AddProduct';
import './CommonStyles.css';
import './AddSellStyles.css';

const today = () => dateUtils.getChicagoISOString().split('T')[0];

const normalizeOptions = (value) => {
  if (Array.isArray(value)) return value.map((item) => (typeof item === 'object' ? item : { id: item, name: item }));
  if (value && typeof value === 'object') {
    return Object.entries(value).map(([id, name]) => ({ id, name }));
  }
  return [];
};

const AddPurchaseOrder = ({ onBack }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [form, setForm] = useState({
    contact_id: '',
    location_id: '',
    ref_no: '',
    transaction_date: today(),
    delivery_date: '',
    shipping_status: '',
    tax_id: '',
    tax_amount: 0,
    discount_type: 'Percentage',
    discount_amount: '',
    shipping_charges: '',
    additional_notes: '',
    pay_term_number: '',
    pay_term_type: '',
    document: null,
    purchase_requisition_ids: '',
    shipping_details: '',
    shipping_address: '',
    delivered_to: '',
    shipping_documents: null,
    additional_expense_key_1: '', additional_expense_value_1: '',
    additional_expense_key_2: '', additional_expense_value_2: '',
    additional_expense_key_3: '', additional_expense_value_3: '',
    additional_expense_key_4: '', additional_expense_value_4: '',
    products: [],
  });

  const [suppliers, setSuppliers] = useState([]);
  const [locations, setLocations] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [shippingStatuses, setShippingStatuses] = useState({});
  const [purchaseRequisitions, setPurchaseRequisitions] = useState({});
  const [productOptions, setProductOptions] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState('');
  const [isLoadingProducts, setIsLoadingProducts] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [showAdditionalExpenses, setShowAdditionalExpenses] = useState(false);
  const [showAddSupplierModal, setShowAddSupplierModal] = useState(false);
  const [showAddProductModal, setShowAddProductModal] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await apiService.getPurchaseOrderCreateData();
        if (result.success) {
          const data = result.data || {};
          setLocations(normalizeOptions(data.business_locations));
          setTaxes(data.taxes || []);
          setShippingStatuses(data.shipping_statuses || {});
          if (data.purchase_requisitions) setPurchaseRequisitions(data.purchase_requisitions || {});
        }
      } catch (err) {
        /* console.error('Error fetching create data', err); */
      }
      reloadSuppliers();
    };
    fetchData();
  }, []);

  const reloadSuppliers = async () => {
    try {
      const res = await apiService.getContacts({ type: 'supplier', per_page: 100 });
      const list = Array.isArray(res) ? res : (res.data || []);
      setSuppliers(list);
      return list;
    } catch (err) {
      return [];
    }
  };

  useEffect(() => {
    if (!isEditMode) return;
    const fetchEditOrder = async () => {
      setIsLoadingOrder(true);
      try {
        const res = await apiService.getPurchaseOrderEditData(id);
        const data = res.data || res;
        const purchase = data.purchase || {};

        if (data.business_locations) setLocations(normalizeOptions(data.business_locations));
        if (data.taxes) setTaxes(data.taxes || []);
        if (data.shipping_statuses) setShippingStatuses(data.shipping_statuses || {});
        if (data.purchase_requisitions) setPurchaseRequisitions(data.purchase_requisitions || {});

        const rawDiscType = purchase.discount_type || 'percentage';
        const formattedDiscType = rawDiscType.charAt(0).toUpperCase() + rawDiscType.slice(1);

        const loadedProducts = (purchase.purchase_lines || []).map((pl, idx) => ({
          id: pl.id || Date.now() + idx,
          product_id: pl.product_id,
          variation_id: pl.variation_id,
          name: pl.product?.name || 'Product',
          sku: pl.product?.sku || pl.variations?.sub_sku || '',
          quantity: parseFloat(pl.quantity || 1),
          unit_cost: parseFloat(pl.purchase_price || pl.pp_without_discount || 0),
          tax_percent: 0,
        }));

        setForm({
          contact_id: purchase.contact_id || '',
          location_id: purchase.location_id || '',
          ref_no: purchase.ref_no || '',
          transaction_date: purchase.transaction_date ? purchase.transaction_date.split(' ')[0] : today(),
          delivery_date: purchase.delivery_date ? purchase.delivery_date.split(' ')[0] : '',
          shipping_status: purchase.shipping_status || '',
          tax_id: purchase.tax_id || '',
          tax_amount: parseFloat(purchase.tax_amount || 0),
          discount_type: formattedDiscType,
          discount_amount: purchase.discount_amount || '',
          shipping_charges: purchase.shipping_charges || '',
          additional_notes: purchase.additional_notes || '',
          pay_term_number: purchase.pay_term_number || '',
          pay_term_type: purchase.pay_term_type || '',
          document: null,
          purchase_requisition_ids: Array.isArray(purchase.purchase_requisition_ids) ? purchase.purchase_requisition_ids[0] : (purchase.purchase_requisition_ids || ''),
          shipping_details: purchase.shipping_details || '',
          shipping_address: purchase.shipping_address || '',
          delivered_to: purchase.delivered_to || '',
          shipping_documents: null,
          additional_expense_key_1: purchase.additional_expense_key_1 || '',
          additional_expense_value_1: purchase.additional_expense_value_1 || '',
          additional_expense_key_2: purchase.additional_expense_key_2 || '',
          additional_expense_value_2: purchase.additional_expense_value_2 || '',
          additional_expense_key_3: purchase.additional_expense_key_3 || '',
          additional_expense_value_3: purchase.additional_expense_value_3 || '',
          additional_expense_key_4: purchase.additional_expense_key_4 || '',
          additional_expense_value_4: purchase.additional_expense_value_4 || '',
          products: loadedProducts,
        });

        if (purchase.additional_expense_value_1 || purchase.additional_expense_value_2 || purchase.additional_expense_value_3 || purchase.additional_expense_value_4) {
          setShowAdditionalExpenses(true);
        }
      } catch (err) {
        /* console.error('Failed to load purchase order for editing:', err); */
        window.alert('Failed to load purchase order for editing: ' + (err.message || 'Unknown error'));
      } finally {
        setIsLoadingOrder(false);
      }
    };
    fetchEditOrder();
  }, [id, isEditMode]);

  const searchProducts = async (term) => {
    if (!term.trim()) {
      setProductOptions([]);
      return;
    }
    setIsLoadingProducts(true);
    try {
      const res = await apiService.getProducts({ search: term, per_page: 50 });
      const list = Array.isArray(res) ? res : (res.data || []);
      setProductOptions(list);
    } catch (err) {
      /* console.error('Error searching products:', err); */
      setProductOptions([]);
    } finally {
      setIsLoadingProducts(false);
    }
  };

  useEffect(() => {
    if (!isEditMode && locations.length > 0 && !form.location_id) {
      setForm((prev) => ({ ...prev, location_id: locations[0].id }));
    }
  }, [locations, isEditMode, form.location_id]);

  useEffect(() => {
    const fetchRequisitionsForLocation = async () => {
      try {
        const params = form.location_id ? { location_id: form.location_id } : {};
        const res = await apiService.getPurchaseOrderCreateData(params);
        if (res && res.success && res.data?.purchase_requisitions) {
          setPurchaseRequisitions(res.data.purchase_requisitions || {});
        }
      } catch (err) {
        /* console.error('Error fetching requisitions for location:', err); */
      }
    };
    fetchRequisitionsForLocation();
  }, [form.location_id]);

  useEffect(() => {
    const timer = setTimeout(() => searchProducts(productSearchTerm), 400);
    return () => clearTimeout(timer);
  }, [productSearchTerm]);

  const handleChange = (key, value) => {
    setForm((prev) => {
      const updated = { ...prev, [key]: value };
      if (key === 'tax_id') {
        const tax = taxes.find((t) => String(t.id) === String(value));
        updated.tax_amount = tax ? (subtotal * (parseFloat(tax.amount) || 0) / 100) : 0;
      }
      if (key === 'contact_id' && value) {
        const supplier = suppliers.find((s) => String(s.id) === String(value));
        if (supplier) {
          const addrParts = [
            supplier.address_line_1,
            supplier.address_line_2,
            supplier.city,
            supplier.state,
            supplier.country,
            supplier.zip_code,
            supplier.landmark
          ].filter(Boolean);
          if (addrParts.length > 0) {
            updated.shipping_address = addrParts.join(', ');
          }
          if (supplier.pay_term_number || supplier.pay_term_days) {
            updated.pay_term_number = supplier.pay_term_number || supplier.pay_term_days;
          }
          if (supplier.pay_term_type) {
            updated.pay_term_type = supplier.pay_term_type.toLowerCase();
          } else if (supplier.pay_term_days) {
            updated.pay_term_type = 'days';
          }
        }
      }
      return updated;
    });
  };

  const addProduct = (product) => {
    const exists = form.products.find((p) => String(p.product_id) === String(product.id));
    if (exists) return;
    const variation = product.product_variations?.[0]?.variations?.[0] || product.variations?.[0] || {};
    const unitCost = parseFloat(
      product.purchase_price_inc_tax ||
      product.default_purchase_price ||
      product.purchase_price ||
      variation.default_purchase_price ||
      variation.dpp_inc_tax ||
      product.price ||
      0
    );
    const stockVal = product.qty_available !== undefined ? product.qty_available : (product.current_stock !== undefined ? product.current_stock : (variation.qty_available !== undefined ? variation.qty_available : undefined));
    setForm((prev) => ({
      ...prev,
      products: [
        ...prev.products,
        {
          id: Date.now(),
          product_id: product.id,
          variation_id: variation.id || product.variation_id || product.id,
          name: product.name || product.product || 'Product',
          sku: product.sku || variation.sub_sku || '',
          quantity: 1,
          unit_cost: unitCost,
          tax_percent: 0,
          discount_percent: 0,
          tax_id: product.tax_id || '',
          stock: stockVal,
        },
      ],
    }));
    setProductSearchTerm('');
  };

  const updateProduct = (id, key, value) => {
    setForm((prev) => ({
      ...prev,
      products: prev.products.map((p) => (p.id !== id ? p : { ...p, [key]: value })),
    }));
  };

  const removeProduct = (id) => setForm((prev) => ({ ...prev, products: prev.products.filter((p) => p.id !== id) }));

  const subtotal = form.products.reduce((s, p) => s + (parseFloat(p.quantity) || 0) * (parseFloat(p.unit_cost) || 0), 0);
  const discountVal = parseFloat(form.discount_amount) || 0;
  const discountAmt = form.discount_type === 'Percentage' ? subtotal * discountVal / 100 : discountVal;
  const selectedTax = taxes.find((t) => String(t.id) === String(form.tax_id));
  const taxRate = selectedTax ? parseFloat(selectedTax.amount) || 0 : 0;
  const taxAmt = (subtotal - discountAmt) * taxRate / 100;
  const shipping = parseFloat(form.shipping_charges) || 0;
  const exp1 = parseFloat(form.additional_expense_value_1) || 0;
  const exp2 = parseFloat(form.additional_expense_value_2) || 0;
  const exp3 = parseFloat(form.additional_expense_value_3) || 0;
  const exp4 = parseFloat(form.additional_expense_value_4) || 0;
  const totalAdditionalExpenses = exp1 + exp2 + exp3 + exp4;
  const finalTotal = Math.max(0, subtotal - discountAmt + taxAmt + shipping + totalAdditionalExpenses);

  const validate = () => {
    const newErrors = {};
    if (!form.contact_id) newErrors.contact_id = 'Supplier is required';
    if (!form.location_id) newErrors.location_id = 'Business location is required';
    if (!form.transaction_date) newErrors.transaction_date = 'Date is required';
    if (form.products.length === 0) newErrors.products = 'Please add at least one product';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    const transaction_data = {
      ...form,
      total_before_tax: subtotal.toFixed(2),
      tax_amount: taxAmt.toFixed(2),
      final_total: finalTotal.toFixed(2),
      discount_type: (form.discount_type || 'percentage').toLowerCase(),
      purchases: form.products.map((p) => ({
        product_id: p.product_id,
        variation_id: p.variation_id,
        quantity: p.quantity,
        purchase_price: p.unit_cost,
        purchase_price_inc_tax: p.unit_cost,
        pp_without_discount: p.unit_cost,
        item_tax: 0,
      })),
    };
    return transaction_data;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      let result;
      if (isEditMode) {
        result = await apiService.updatePurchaseOrder(id, buildPayload());
      } else {
        result = await apiService.createPurchaseOrder(buildPayload());
      }
      if (result.success || result.status === 'success' || result.id) {
        toast.success(isEditMode ? 'Purchase order updated successfully.' : 'Purchase order saved successfully.');
        if (onBack) onBack();
        else navigate('/purchase_order');
      } else {
        toast.error(result.message || result.msg || 'Failed to save purchase order');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to save purchase order');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="sell-form-container add-sell-override">
      <div className="page-header">
        <button
          type="button"
          onClick={onBack || (() => navigate('/purchase_order'))}
          className="back-button"
          style={{
            background: 'white',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            padding: '0.5rem 0.75rem',
            cursor: 'pointer',
            fontSize: '1.2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '40px',
            height: '40px',
            color: '#4f46e5'
          }}
          title="Back to Purchase Orders"
        >
          ←
        </button>
        <h2 style={{ fontSize: '1.25rem', color: '#4f46e5', fontWeight: 600, margin: 0 }}>
          {isEditMode ? 'Edit Purchase Order' : 'Add Purchase Order'}
          {isLoadingOrder && (
            <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 'normal', fontStyle: 'italic', marginLeft: '0.5rem' }}>
              (Loading data...)
            </span>
          )}
        </h2>
      </div>

      <form className="sell-form" onSubmit={(e) => { e.preventDefault(); handleSave(); }}>
        {/* Main Info Card */}
        <div className="form-section-content" style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Supplier:*</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <select
                  className={`form-select ${errors.contact_id ? 'error' : ''}`}
                  value={form.contact_id}
                  onChange={(e) => handleChange('contact_id', e.target.value)}
                  style={{ flex: 1 }}
                >
                  <option value="">Please Select</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={s.id}>{s.supplier_business_name || s.name}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => setShowAddSupplierModal(true)}
                  style={{
                    background: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '26px',
                    height: '26px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    fontSize: '1rem',
                    fontWeight: 'bold'
                  }}
                  title="Add Supplier"
                >
                  +
                </button>
              </div>
              {errors.contact_id && <span className="error-text">{errors.contact_id}</span>}
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Reference No:</label>
              <input type="text" className="form-input" value={form.ref_no} onChange={(e) => handleChange('ref_no', e.target.value)} />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Order date:*</label>
              <input
                type="date"
                className={`form-input ${errors.transaction_date ? 'error' : ''}`}
                value={form.transaction_date}
                onChange={(e) => handleChange('transaction_date', e.target.value)}
              />
              {errors.transaction_date && <span className="error-text">{errors.transaction_date}</span>}
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Delivery date:</label>
              <input type="date" className="form-input" value={form.delivery_date} onChange={(e) => handleChange('delivery_date', e.target.value)} />
            </div>
          </div>

          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Business Location:*</label>
              <select
                className={`form-select ${errors.location_id ? 'error' : ''}`}
                value={form.location_id}
                onChange={(e) => handleChange('location_id', e.target.value)}
              >
                <option value="">Please Select</option>
                {locations.map((loc) => (
                  <option key={loc.id} value={loc.id}>{loc.name}</option>
                ))}
              </select>
              {errors.location_id && <span className="error-text">{errors.location_id}</span>}
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Pay term:</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  type="number"
                  className="form-input"
                  placeholder="Pay term"
                  value={form.pay_term_number}
                  onChange={(e) => handleChange('pay_term_number', e.target.value)}
                  style={{ width: '60%' }}
                />
                <select
                  className="form-select"
                  value={form.pay_term_type}
                  onChange={(e) => handleChange('pay_term_type', e.target.value)}
                  style={{ width: '40%' }}
                >
                  <option value="">Please Select</option>
                  <option value="months">Months</option>
                  <option value="days">Days</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Attach Document:</label>
              <input
                type="file"
                className="form-input"
                onChange={(e) => handleChange('document', e.target.files[0])}
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Purchase Requisition:</label>
              <select
                className="form-select"
                value={form.purchase_requisition_ids}
                onChange={(e) => handleChange('purchase_requisition_ids', e.target.value)}
              >
                <option value="">Please Select</option>
                {Array.isArray(purchaseRequisitions)
                  ? purchaseRequisitions.map((req) => (
                      <option key={req.id} value={req.id}>{req.ref_no || req.text || req.name}</option>
                    ))
                  : Object.entries(purchaseRequisitions).map(([reqId, reqRef]) => (
                      <option key={reqId} value={reqId}>{reqRef}</option>
                    ))}
              </select>
            </div>
          </div>
        </div>

        {/* Product Table Card */}
        <div className="form-section-content" style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
          <div className="form-row" style={{ marginBottom: '1rem' }}>
            <div className="form-group full-width" style={{ position: 'relative' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #cbd5e1', borderRadius: '6px', overflow: 'hidden', flex: 1 }}>
                  <span style={{ padding: '0.6rem 0.8rem', background: '#f8fafc', color: '#64748b' }}>🔍</span>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter Product name / SKU / Scan bar code"
                    value={productSearchTerm}
                    onChange={(e) => setProductSearchTerm(e.target.value)}
                    style={{ border: 'none', borderRadius: 0 }}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => setShowAddProductModal(true)}
                  style={{
                    background: '#2563eb',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '50%',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    flexShrink: 0,
                    fontSize: '1.2rem',
                    fontWeight: 'bold'
                  }}
                  title="Add Product"
                >
                  +
                </button>
              </div>
              {productSearchTerm && (
                <div
                  className="product-search-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    background: '#ffffff',
                    border: '1px solid #cbd5e1',
                    borderRadius: '8px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                    zIndex: 9999,
                    maxHeight: '260px',
                    overflowY: 'auto',
                    marginTop: '4px',
                    padding: '0.25rem 0'
                  }}
                >
                  {isLoadingProducts ? (
                    <div style={{ padding: '0.75rem 1rem', color: '#64748b', fontSize: '0.875rem' }}>⏳ Searching products...</div>
                  ) : productOptions.length === 0 ? (
                    <div style={{ padding: '0.75rem 1rem', color: '#94a3b8', fontSize: '0.875rem', fontStyle: 'italic' }}>No products found</div>
                  ) : (
                    productOptions.map((p) => {
                      const variation = p.product_variations?.[0]?.variations?.[0] || {};
                      const skuVal = p.sku || p.sub_sku || variation.sub_sku || 'N/A';
                      const priceVal = parseFloat(
                        p.purchase_price_inc_tax ||
                        p.default_purchase_price ||
                        variation.dpp_inc_tax ||
                        variation.default_purchase_price ||
                        p.purchase_price ||
                        0
                      );
                      return (
                        <div
                          key={p.id}
                          onClick={() => addProduct(p)}
                          style={{
                            padding: '0.6rem 1rem',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid #f1f5f9'
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.background = '#f8fafc')}
                          onMouseLeave={(e) => (e.currentTarget.style.background = '#ffffff')}
                        >
                          <div>
                            <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#1e293b' }}>
                              {p.name || p.product}
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                              SKU: <span style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{skuVal}</span>
                            </div>
                          </div>
                          <div style={{ fontWeight: '600', fontSize: '0.875rem', color: '#2563eb' }}>
                            ${priceVal.toFixed(2)}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          </div>

          {errors.products && <div className="error-text mb-2" style={{ color: '#ef4444' }}>{errors.products}</div>}

          <div className="product-table" style={{ overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
            <table style={{ minWidth: '1550px', width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
              <thead>
                <tr style={{ background: '#22c55e', color: '#ffffff', textAlign: 'left', fontSize: '0.85rem' }}>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '35px' }}>#</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '200px' }}>Product Name</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '170px' }}>Order quantity</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '105px' }}>Unit Cost (BD)</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '85px' }}>Discount Percent</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '105px' }}>Unit Cost (BT)</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '105px' }}>Subtotal (BT)</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '160px' }}>Product Tax</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '105px' }}>Net Cost</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '100px' }}>Line Total</th>
                  <th style={{ padding: '0.75rem 0.6rem', minWidth: '40px', textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {isLoadingOrder ? (
                  <tr><td colSpan={11} className="no-products" style={{ padding: '1rem', textAlign: 'center', color: '#64748b', fontStyle: 'italic' }}>⏳ Loading order items...</td></tr>
                ) : form.products.length === 0 ? (
                  <tr><td colSpan={11} className="no-products" style={{ padding: '1rem', textAlign: 'center', color: '#94a3b8' }}>No products added</td></tr>
                ) : (
                  form.products.map((p, idx) => {
                    const qty = parseFloat(p.quantity) || 0;
                    const unitCost = parseFloat(p.unit_cost) || 0;
                    const discPercent = parseFloat(p.discount_percent || p.tax_percent) || 0;
                    const costBeforeTax = unitCost * (1 - discPercent / 100);
                    const subtotalBeforeTax = qty * costBeforeTax;

                    const itemTaxObj = taxes.find((t) => String(t.id) === String(p.tax_id));
                    const taxPercentRate = itemTaxObj ? parseFloat(itemTaxObj.amount) || 0 : 0;
                    const itemTaxAmount = costBeforeTax * (taxPercentRate / 100);
                    const netCost = costBeforeTax + itemTaxAmount;
                    const lineTotal = qty * netCost;

                    return (
                      <tr key={p.id} style={{ borderBottom: '1px solid #e2e8f0', fontSize: '0.85rem' }}>
                        <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center', color: '#64748b' }}>{idx + 1}</td>
                        <td style={{ padding: '0.6rem 0.5rem' }}>
                          <div style={{ fontWeight: '600', color: '#1e293b' }}>{p.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{p.sku ? `(${p.sku})` : ''}</div>
                          <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{p.stock !== undefined ? `Current stock: ${p.stock} Single` : ''}</div>
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <input
                              type="number"
                              className="form-input"
                              value={p.quantity}
                              min="0"
                              step="any"
                              onChange={(e) => updateProduct(p.id, 'quantity', e.target.value)}
                              style={{ width: '70px', padding: '0.35rem 0.4rem', flexShrink: 0 }}
                            />
                            <select
                              className="form-select"
                              value={p.unit_name || 'Single'}
                              onChange={(e) => updateProduct(p.id, 'unit_name', e.target.value)}
                              style={{ fontSize: '0.75rem', padding: '0.35rem 0.4rem', width: '80px', flexShrink: 0 }}
                            >
                              <option value="Single">{p.unit_name || 'Single'}</option>
                              {p.sub_units && p.sub_units.map((u) => (
                                <option key={u.id} value={u.actual_name || u.name}>{u.actual_name || u.name}</option>
                              ))}
                            </select>
                          </div>
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={p.unit_cost}
                            min="0"
                            step="any"
                            onChange={(e) => updateProduct(p.id, 'unit_cost', e.target.value)}
                            style={{ width: '90px', padding: '0.35rem 0.4rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={p.discount_percent || 0}
                            min="0"
                            step="any"
                            onChange={(e) => updateProduct(p.id, 'discount_percent', e.target.value)}
                            style={{ width: '70px', padding: '0.35rem 0.4rem' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={costBeforeTax.toFixed(2)}
                            readOnly
                            style={{ width: '90px', padding: '0.35rem 0.4rem', background: '#f8fafc' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem', fontWeight: '600' }}>
                          {subtotalBeforeTax.toFixed(2)}
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem', minWidth: '160px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                            <select
                              className="form-select"
                              value={p.tax_id || ''}
                              onChange={(e) => updateProduct(p.id, 'tax_id', e.target.value)}
                              style={{ fontSize: '0.75rem', width: '100px', flexShrink: 0 }}
                            >
                              <option value="">None</option>
                              {taxes.map((t) => (
                                <option key={t.id} value={t.id}>{t.name}</option>
                              ))}
                            </select>
                            <span style={{ fontSize: '0.8rem', color: '#64748b', fontWeight: '500', whiteSpace: 'nowrap' }}>
                              {itemTaxAmount.toFixed(2)}
                            </span>
                          </div>
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem' }}>
                          <input
                            type="number"
                            className="form-input"
                            value={netCost.toFixed(2)}
                            readOnly
                            style={{ width: '90px', padding: '0.35rem 0.4rem', background: '#f8fafc' }}
                          />
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem', fontWeight: '700', color: '#15803d' }}>
                          {lineTotal.toFixed(2)}
                        </td>
                        <td style={{ padding: '0.6rem 0.5rem', textAlign: 'center' }}>
                          <button type="button" className="remove-product-btn" onClick={() => removeProduct(p.id)} style={{ background: '#ef4444', color: '#ffffff', border: 'none', borderRadius: '4px', width: '22px', height: '22px', cursor: 'pointer', fontWeight: 'bold' }}>✕</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '2rem', fontSize: '0.9rem' }}>
            <div><strong>Total Items:</strong> {form.products.reduce((s, p) => s + (parseFloat(p.quantity) || 0), 0).toFixed(2)}</div>
            <div><strong>Net Total Amount:</strong> ${subtotal.toFixed(2)}</div>
          </div>
        </div>

        {/* Shipping Card */}
        <div className="form-section-content" style={{ background: '#ffffff', padding: '1.25rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1.25rem' }}>
          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginBottom: '1rem' }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Shipping Details:</label>
              <textarea className="form-input" rows={2} placeholder="Shipping Details" value={form.shipping_details} onChange={(e) => handleChange('shipping_details', e.target.value)} />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Shipping Address:</label>
              <textarea className="form-input" rows={2} placeholder="Shipping Address" value={form.shipping_address} onChange={(e) => handleChange('shipping_address', e.target.value)} />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Shipping Charges:</label>
              <input type="number" className="form-input" value={form.shipping_charges} onChange={(e) => handleChange('shipping_charges', e.target.value)} />
            </div>
          </div>

          <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1rem', alignItems: 'start' }}>
            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Shipping Status:</label>
              <select className="form-select" value={form.shipping_status} onChange={(e) => handleChange('shipping_status', e.target.value)}>
                <option value="">Please Select</option>
                {Object.entries(shippingStatuses).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Delivered To:</label>
              <input type="text" className="form-input" placeholder="Delivered To" value={form.delivered_to} onChange={(e) => handleChange('delivered_to', e.target.value)} />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Shipping Documents:</label>
              <input
                type="file"
                className="form-input"
                onChange={(e) => handleChange('shipping_documents', e.target.files[0])}
              />
            </div>

            <div className="form-group">
              <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Additional Notes:</label>
              <textarea className="form-input" rows={2} placeholder="Additional Notes" value={form.additional_notes} onChange={(e) => handleChange('additional_notes', e.target.value)} style={{ width: '100%', resize: 'vertical' }} />
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', margin: '1rem 0' }}>
            <button
              type="button"
              onClick={() => setShowAdditionalExpenses(!showAdditionalExpenses)}
              style={{ background: '#4f46e5', color: '#ffffff', border: 'none', borderRadius: '20px', padding: '0.5rem 1.25rem', cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem' }}
            >
              + {showAdditionalExpenses ? 'Hide additional expenses' : 'Add additional expenses'}
            </button>

            <div style={{ fontSize: '1rem', fontWeight: 700 }}>
              Order Total: ${finalTotal.toFixed(2)}
            </div>
          </div>

          {showAdditionalExpenses && (
            <div className="additional-expenses-box" style={{ background: '#f8fafc', padding: '1rem', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '1rem' }}>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 1 Name:</label>
                  <input type="text" className="form-input" value={form.additional_expense_key_1} onChange={(e) => handleChange('additional_expense_key_1', e.target.value)} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 1 Amount:</label>
                  <input type="number" className="form-input" value={form.additional_expense_value_1} onChange={(e) => handleChange('additional_expense_value_1', e.target.value)} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 2 Name:</label>
                  <input type="text" className="form-input" value={form.additional_expense_key_2} onChange={(e) => handleChange('additional_expense_key_2', e.target.value)} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 2 Amount:</label>
                  <input type="number" className="form-input" value={form.additional_expense_value_2} onChange={(e) => handleChange('additional_expense_value_2', e.target.value)} />
                </div>
              </div>
              <div className="form-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginTop: '0.5rem' }}>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 3 Name:</label>
                  <input type="text" className="form-input" value={form.additional_expense_key_3} onChange={(e) => handleChange('additional_expense_key_3', e.target.value)} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 3 Amount:</label>
                  <input type="number" className="form-input" value={form.additional_expense_value_3} onChange={(e) => handleChange('additional_expense_value_3', e.target.value)} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 4 Name:</label>
                  <input type="text" className="form-input" value={form.additional_expense_key_4} onChange={(e) => handleChange('additional_expense_key_4', e.target.value)} />
                </div>
                <div className="form-group">
                  <label style={{ fontSize: '0.8rem' }}>Expense 4 Amount:</label>
                  <input type="number" className="form-input" value={form.additional_expense_value_4} onChange={(e) => handleChange('additional_expense_value_4', e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Action Button Section */}
        <div className="form-section-content action-buttons-section" style={{ textAlign: 'center', marginTop: '1rem' }}>
          <button type="submit" className="save-btn" disabled={isSaving} style={{ background: '#2563eb', color: '#ffffff', padding: '0.75rem 2.5rem', borderRadius: '8px', fontSize: '1rem', fontWeight: 700, border: 'none', cursor: 'pointer' }}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>

      {/* Add Supplier Modal */}
      {showAddSupplierModal && (
        <div className="modal-overlay" onClick={() => setShowAddSupplierModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', width: '1200px', maxHeight: '95vh', overflowY: 'auto', padding: 0 }}>
            <div className="modal-header" style={{ padding: '15px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Add New Supplier</h2>
              <button className="close-button" onClick={() => setShowAddSupplierModal(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer' }}>&times;</button>
            </div>
            <div style={{ padding: '20px' }}>
              <AddSupplier
                initialType="supplier"
                onSuccess={(newContact) => {
                  setShowAddSupplierModal(false);
                  reloadSuppliers().then((list) => {
                    if (newContact?.id) {
                      handleChange('contact_id', newContact.id);
                    }
                  });
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Add Product Modal */}
      {showAddProductModal && (
        <div className="modal-overlay" onClick={() => setShowAddProductModal(false)} style={{ zIndex: 9999 }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95%', width: '1200px', maxHeight: '95vh', overflowY: 'auto', padding: '20px', position: 'relative' }}>
            <button className="close-button" onClick={() => setShowAddProductModal(false)} style={{ position: 'absolute', top: '15px', right: '20px', background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', zIndex: 10000 }}>&times;</button>
            <AddProduct
              onSuccess={() => {
                setShowAddProductModal(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddPurchaseOrder;
